import { ProCard } from '@ant-design/pro-components';
import { useAccess } from '@umijs/max';
import { useSession } from '@zhiyue/core';
import { Alert, Button, Pagination, Space, Tag, Typography } from 'antd';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Empty from '@/components/QuietEmpty';
import {
  queryBusinessTripItems,
  queryBusinessTripPlans,
} from '@/services/bff/business-trip.api';
import type {
  BusinessTripItem,
  BusinessTripPlan,
  BusinessTripRelationPage,
  CrmRelationObjectType,
} from '@/services/bff/types/business-trip';
import { COLLABORATION_CHANGED } from '@/services/collaborationEvents';
import { TripDetail, TripEditor, tripAllowed } from '../business-trip-manager';

const { Paragraph, Text } = Typography;
const PAGE_SIZE = 20;
const EMPTY_PAGE: BusinessTripRelationPage<never> = {
  data: [],
  total: 0,
  page: 1,
  pageSize: PAGE_SIZE,
};

type BusinessTripRelationPanelProps = {
  title?: string;
  objectType: CrmRelationObjectType;
  objectUuid?: string;
  className?: string;
};

const planStatusLabels: Record<string, string> = {
  draft: '草稿',
  in_progress: '进行中',
  completed: '已完成',
  canceled: '已取消',
};

const itemStatusLabels: Record<string, string> = {
  planning: '待编排',
  included: '已纳入计划',
  completed: '已完成',
  closed: '已关闭',
  canceled: '已关闭',
  assessable: '待评估',
  deferred: '已延期',
};

const priorityLabels: Record<string, string> = {
  low: '低优先级',
  medium: '中优先级',
  high: '高优先级',
};

const executionStatusLabels: Record<string, string> = {
  scheduled: '待执行',
  in_progress: '执行中',
  completed: '已完成',
  canceled: '已取消',
};

const slotLabels: Record<string, string> = {
  morning: '上午',
  afternoon: '下午',
  evening: '晚上',
  all_day: '全天',
  custom: '自定义时段',
};

const formatDateRange = (start?: string | null, end?: string | null) => {
  if (!start && !end) return '日期待定';
  if (!end || start === end) return start || end || '日期待定';
  return `${start} 至 ${end}`;
};

const errorMessage = (error: unknown) => {
  const status =
    (error as { status?: number })?.status ||
    (error as { response?: { status?: number } })?.response?.status;
  if (status === 401) return '登录已失效，请刷新登录状态后重试。';
  if (status === 403) return '你没有查看相关出差数据的权限。';
  if (status === 502 || status === 503) {
    return '出差助手暂时不可用，请稍后重试。';
  }
  return '相关出差数据加载失败，请重试。';
};

const statusTag = (
  value: string | null | undefined,
  labels: Record<string, string>,
) => {
  if (!value) return null;
  const color =
    value === 'completed'
      ? 'green'
      : value === 'canceled' || value === 'closed'
        ? 'default'
        : value === 'high'
          ? 'red'
          : 'blue';
  return <Tag color={color}>{labels[value] || value}</Tag>;
};

export function BusinessTripRelationPanel({
  objectType,
  objectUuid,
  className,
  title,
}: BusinessTripRelationPanelProps) {
  const access = useAccess() as Record<string, boolean>;
  const { currentUser } = useSession();
  const contextKey = [
    currentUser?.tenantUuid,
    (currentUser as API.CurrentUser)?.organizationUuid,
    currentUser?.memberUuid,
  ].join('|');
  const [detail, setDetail] = useState<{
    kind: 'items' | 'plans';
    uuid: string;
  }>();
  const [creating, setCreating] = useState(false);
  const [items, setItems] =
    useState<BusinessTripRelationPage<BusinessTripItem>>(EMPTY_PAGE);
  const [plans, setPlans] =
    useState<BusinessTripRelationPage<BusinessTripPlan>>(EMPTY_PAGE);
  const itemsVersion = useRef(0);
  const plansVersion = useRef(0);
  const viewKey = `${contextKey}:${objectType}:${objectUuid}`;
  const [itemsKey, setItemsKey] = useState('');
  const [plansKey, setPlansKey] = useState('');
  const visibleItems = itemsKey === viewKey ? items : EMPTY_PAGE;
  const visiblePlans = plansKey === viewKey ? plans : EMPTY_PAGE;
  const [itemsLoading, setItemsLoading] = useState(false);
  const [plansLoading, setPlansLoading] = useState(false);
  const [itemsError, setItemsError] = useState<unknown>();
  const [plansError, setPlansError] = useState<unknown>();

  const loadItems = useCallback(
    async (page: number) => {
      if (!objectUuid) return;
      const generation = ++itemsVersion.current;
      setItemsLoading(true);
      setItemsError(undefined);
      try {
        const result = await queryBusinessTripItems({
          objectType,
          objectUuid,
          page,
          pageSize: PAGE_SIZE,
        });
        if (generation === itemsVersion.current) {
          setItems(result);
          setItemsKey(viewKey);
        }
      } catch (error) {
        if (generation === itemsVersion.current) setItemsError(error);
      } finally {
        if (generation === itemsVersion.current) setItemsLoading(false);
      }
    },
    [viewKey],
  );
  const loadPlans = useCallback(
    async (page: number) => {
      if (!objectUuid) return;
      const generation = ++plansVersion.current;
      setPlansLoading(true);
      setPlansError(undefined);
      try {
        const result = await queryBusinessTripPlans({
          objectType,
          objectUuid,
          page,
          pageSize: PAGE_SIZE,
        });
        if (generation === plansVersion.current) {
          setPlans(result);
          setPlansKey(viewKey);
        }
      } catch (error) {
        if (generation === plansVersion.current) setPlansError(error);
      } finally {
        if (generation === plansVersion.current) setPlansLoading(false);
      }
    },
    [viewKey],
  );
  useEffect(() => {
    if (!objectUuid) return;
    void loadItems(1);
    void loadPlans(1);
    const refresh = () => {
      void loadItems(1);
      void loadPlans(1);
    };
    window.addEventListener(COLLABORATION_CHANGED, refresh);
    return () => {
      itemsVersion.current++;
      plansVersion.current++;
      window.removeEventListener(COLLABORATION_CHANGED, refresh);
    };
  }, [loadItems, loadPlans, objectUuid]);

  if (!objectUuid) {
    return (
      <ProCard title={title || '相关出差'} className={className}>
        <Empty description={false} />
      </ProCard>
    );
  }

  return (
    <div className={className}>
      {title && <Typography.Title level={5}>{title}</Typography.Title>}
      {tripAllowed(access, 'create') && (
        <Button onClick={() => setCreating(true)}>新建关联出差事项</Button>
      )}
      {creating && (
        <TripEditor
          kind="items"
          relation={{ objectType, objectUuid }}
          onClose={() => setCreating(false)}
          onSaved={(row) => setDetail({ kind: 'items', uuid: row.uuid })}
        />
      )}
      {detail && (
        <TripDetail
          key={`${contextKey}:${detail.uuid}`}
          {...detail}
          onClose={() => setDetail(undefined)}
        />
      )}
      <ProCard title="相关出差计划" loading={plansLoading}>
        {plansError ? (
          <Alert
            type="error"
            showIcon
            title={errorMessage(plansError)}
            action={
              <Button
                size="small"
                onClick={() => void loadPlans(visiblePlans.page)}
              >
                重试
              </Button>
            }
          />
        ) : visiblePlans.data.length ? (
          <>
            <div>
              {visiblePlans.data.map((plan) => (
                <div
                  key={plan.uuid}
                  style={{
                    padding: '12px 0',
                    borderBottom: '1px solid var(--ant-color-border-secondary)',
                  }}
                >
                  <Space
                    orientation="vertical"
                    size={4}
                    style={{ width: '100%' }}
                  >
                    <Space wrap>
                      <Button
                        type="link"
                        disabled={!access['business-trips']}
                        onClick={() =>
                          setDetail({ kind: 'plans', uuid: plan.uuid })
                        }
                      >
                        {plan.title}
                      </Button>
                      {statusTag(plan.status, planStatusLabels)}
                    </Space>
                    <Text type="secondary">
                      {plan.destinationCity || '目的地待定'} ·{' '}
                      {formatDateRange(plan.startDate, plan.endDate)}
                    </Text>
                    {plan.tripObjective ? (
                      <Paragraph ellipsis={{ rows: 2 }}>
                        {plan.tripObjective}
                      </Paragraph>
                    ) : null}
                    {plan.matchedItems?.length ? (
                      <Space orientation="vertical" size={2}>
                        <Text type="secondary">关联事项</Text>
                        {plan.matchedItems.map((item) => (
                          <Space key={item.uuid} wrap>
                            <Text>{item.title}</Text>
                            {statusTag(
                              item.executionStatus || item.status,
                              executionStatusLabels,
                            )}
                            <Text type="secondary">
                              {formatDateRange(
                                item.executionStartDate,
                                item.executionEndDate,
                              )}
                              {item.executionStartTimeSlot
                                ? ` · ${slotLabels[item.executionStartTimeSlot] || item.executionStartTimeSlot}`
                                : ''}
                              {item.locationNote
                                ? ` · ${item.locationNote}`
                                : ''}
                            </Text>
                          </Space>
                        ))}
                      </Space>
                    ) : null}
                  </Space>
                </div>
              ))}
            </div>
            <Pagination
              current={visiblePlans.page}
              pageSize={visiblePlans.pageSize}
              total={visiblePlans.total}
              showSizeChanger={false}
              size="small"
              onChange={(page) => void loadPlans(page)}
            />
          </>
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={false} />
        )}
      </ProCard>

      <ProCard
        title="相关出差事项"
        loading={itemsLoading}
        style={{ marginTop: 16 }}
      >
        {itemsError ? (
          <Alert
            type="error"
            showIcon
            title={errorMessage(itemsError)}
            action={
              <Button
                size="small"
                onClick={() => void loadItems(visibleItems.page)}
              >
                重试
              </Button>
            }
          />
        ) : visibleItems.data.length ? (
          <>
            <div>
              {visibleItems.data.map((item) => (
                <div key={item.uuid}>
                  <Button
                    type="link"
                    disabled={!access['business-trips']}
                    onClick={() =>
                      setDetail({ kind: 'items', uuid: item.uuid })
                    }
                  >
                    查看出差详情
                  </Button>
                  <BusinessTripItemRow item={item} />
                </div>
              ))}
            </div>
            <Pagination
              current={visibleItems.page}
              pageSize={visibleItems.pageSize}
              total={visibleItems.total}
              showSizeChanger={false}
              size="small"
              onChange={(page) => void loadItems(page)}
            />
          </>
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={false} />
        )}
      </ProCard>
    </div>
  );
}

function BusinessTripItemRow({ item }: { item: BusinessTripItem }) {
  return (
    <div
      style={{
        padding: '12px 0',
        borderBottom: '1px solid var(--ant-color-border-secondary)',
      }}
    >
      <Space orientation="vertical" size={4}>
        <Space wrap>
          <Text strong>{item.title}</Text>
          {statusTag(item.status, itemStatusLabels)}
          {statusTag(item.priority, priorityLabels)}
        </Space>
        <Text type="secondary">
          {item.destinationCity || '目的地待定'}
          {item.businessType ? ` · ${item.businessType}` : ''}
        </Text>
        {item.businessGoal ? (
          <Text type="secondary">目标：{item.businessGoal}</Text>
        ) : null}
        {item.plan ? (
          <Text type="secondary">
            所属计划：{item.plan.title}（
            {formatDateRange(item.plan.startDate, item.plan.endDate)}）
          </Text>
        ) : null}
      </Space>
    </div>
  );
}
